import React, { useState, useEffect } from "react";
import "../styles/Formularios.css";
import { useGlobal } from "../context/ContenedorGlobal";
import { useApiFetch } from "../utils/apiFetch";
import InputBienesAutocomplete from "./InputBienesAutocomplete";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/logo_sict.png";

function ConsultarBienes() {

    const { permisos, setSubModuloActual, setBienSeleccionado, logout } = useGlobal();
    const apiFetch = useApiFetch();
    const [bienes, setBienes] = useState([]);
    const [mensaje, setMensaje] = useState("");
    const [seleccionado, setSeleccionado] = useState(null);
    const [resetAutocomplete, setResetAutocomplete] = useState(false);
    const [bienDetalle, setBienDetalle] = useState(null);

    const tienePermisoConsultar = permisos.includes(18); // id_permiso=18 "Consultar Bienes"

    // Consultar todos los bienes al cargar el componente
    useEffect(() => {
        const fetchBienes = async () => {
            try {
                const data = await apiFetch("consultar_bienes.php", { method: "GET" }, logout);
                if (data.status === "ok") {
                    setBienes(data.bienes);
                } else {
                    setMensaje(data.message || "Error al consultar bienes.");
                }
            } catch (err) {
                setMensaje("Error de conexión: " + err.message);
            }
        };
        fetchBienes();
    }, []);

    // Para acceder a las propiedades del objeto seleccionado y verificar su estado
    useEffect(() => {
        if (seleccionado) {
            const fetchBien = async () => {
                const data = await apiFetch(
                    `consulta_18_1.php?serie_bien=${encodeURIComponent(seleccionado)}`,
                    { method: "GET" },
                    logout
                );
                if (data.status === "ok") {
                    setBienDetalle(data.bien);
                }
            };
            fetchBien();
        } else {
            setBienDetalle(null);
        }
    }, [seleccionado]);

    if (!tienePermisoConsultar) {
        return <p>Acceso denegado. No tiene permiso para consultar bienes.</p>;
    }

    const handleModificar = () => {
        if (seleccionado) {
            setBienSeleccionado(seleccionado);
            setSubModuloActual("Modificar Bienes");
        }
    };

    const handleEliminar = () => {
        if (seleccionado) {
            setBienSeleccionado(seleccionado);
            setSubModuloActual("Eliminar Bienes");
        }
    };

    // Función exportar en CSV
    const handleExportar = async () => {
        const headers = ["Serie", "Ip", "Usuario"];
        let rows = [];

        try {
            if (seleccionado === null) {
                // Exportar todos
                rows = bienes.map(b => [
                    b.serie_bien,
                    b.ip || "",
                    `${b.a_paterno ?? ""} ${b.a_materno ?? ""} ${b.usuario ?? ""}`.trim()
                ]);
            } else {
                // Exportar solo el bien seleccionado
                const data = await apiFetch(
                    `consulta_18_1.php?serie_bien=${encodeURIComponent(seleccionado)}`,
                    { method: "GET" },
                    logout
                );

                if (data.status === "ok") {
                    const bien = data.bien;
                    rows = [[
                        bien.serie_bien,
                        bien.ip || "",
                        `${bien.a_paterno ?? ""} ${bien.a_materno ?? ""} ${bien.usuario ?? ""}`.trim()
                    ]];
                } else {
                    alert("Error al consultar bien: " + (data.message || "Desconocido"));
                    return;
                }
            }

            // Generar CSV
            let csvContent = headers.join(",") + "\r\n";
            rows.forEach(r => {
                csvContent += r.map(v => String(v).replace(/,/g, " ")).join(",") + "\r\n";
            });

            const blob = new Blob([csvContent], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "bienes.csv";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            alert("Error al exportar: " + error.message);
        }
    };

    // Función para exportar en PDF
    const handleExportarPDF = async () => {
        let registrosParaExportar = [];

        if (seleccionado === null) {
            registrosParaExportar = bienes;
        } else {
            const data = await apiFetch(
                `consulta_18_1.php?serie_bien=${encodeURIComponent(seleccionado)}`,
                { method: "GET" },
                logout
            );
            if (data.status === "ok") {
                registrosParaExportar = [data.bien];
            } else {
                alert("Error al consultar bien: " + (data.message || "Desconocido"));
                return;
            }
        }

        const doc = new jsPDF();

        // Logo institucional
        doc.addImage(logo, "PNG", 10, 10, 30, 15);

        // Encabezado
        doc.setFontSize(14);
        doc.text("Secretaría de Infraestructura, Comunicaciones y Transportes", 50, 20);
        doc.setFontSize(12);
        doc.text("Listado de Bienes", 50, 30);

        doc.setLineWidth(0.5);
        doc.line(10, 40, 200, 40);

        // Tabla
        autoTable(doc, {
            startY: 50,
            head: [["Serie", "Ip", "Usuario", "Estado"]],
            body: registrosParaExportar.map(b => [
                b.serie_bien,
                b.ip || "",
                `${b.a_paterno ?? ""} ${b.a_materno ?? ""} ${b.usuario ?? ""}`.trim(),
                b.estado
            ])
        });

        // Pie de página
        doc.setFontSize(10);
        doc.text("© 2025-2026 SIGBI-712. Todos los derechos reservados.", 10, 290);

        const nombreArchivo = seleccionado
            ? `bien_${seleccionado}.pdf`
            : "bienes.pdf";

        doc.save(nombreArchivo);
    };


    const handleLimpiarSeleccion = () => {
        setSeleccionado(null);
        setBienSeleccionado(null);
        setMensaje("");
        setResetAutocomplete(prev => !prev);
    };

    return (
        <div className="sesion-form">

            {mensaje && <p className="error">{mensaje}</p>}

            <InputBienesAutocomplete
                idSeleccionado={seleccionado}
                setIdSeleccionado={setSeleccionado}
                label="Ingrese la serie del bien"
                placeholder="MXL1234ABC"
                title="Máximo 30 caracteres"
                readOnly={false}
                maxLength={30}
                resetEntrada={resetAutocomplete}
            />

            <div className="form-buttons">
                <button disabled={!seleccionado || (bienDetalle && bienDetalle.id_estado === 24)} onClick={handleModificar}>Modificar<br />bien</button>
                <button disabled={!seleccionado || (bienDetalle && bienDetalle.id_estado === 24)} onClick={handleEliminar}>Eliminar<br />bien</button>
                <button className="exportar" onClick={handleExportar} disabled={bienes.length === 0}>
                    Exportar<br></br>CSV
                </button>
                <button className="exportar" onClick={handleExportarPDF} disabled={bienes.length === 0}>
                    Exportar<br></br>PDF
                </button>
                <button onClick={handleLimpiarSeleccion} disabled={!seleccionado}>
                    Limpiar
                </button>
            </div>
        </div>
    );
}

export default ConsultarBienes;